import { mitreUrl, severityPresentation } from '../../lib/detections/semantics.ts'
import type { DetectionSeverity, MitreRef } from '../../lib/sse/types.ts'

/**
 * Selo de severidade: quadrado cheio + palavra. A palavra é o canal primário —
 * "Alta" e "Crítica" são a mesma mancha para quem tem protanopia.
 */
export function SeverityBadge({
  severity,
  size = 'sm',
}: {
  severity: DetectionSeverity | null
  size?: 'sm' | 'lg'
}) {
  const p = severityPresentation(severity)
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 border ${p.border} ${p.text} ${
        size === 'lg' ? 'px-2 py-0.5 text-sm' : 'px-1.5 py-px text-micro'
      } font-medium uppercase tracking-label`}
      title={`Severidade ${p.label.toLowerCase()}`}
    >
      <span aria-hidden className={`inline-block ${size === 'lg' ? 'size-2' : 'size-1.5'} ${p.fill}`} />
      {p.label}
    </span>
  )
}

/** Tom do risco pelo mesmo corte das severidades padrão do Argos (21/47/73/99). */
function riskSeverity(score: number): DetectionSeverity {
  if (score >= 90) return 'critical'
  if (score >= 70) return 'high'
  if (score >= 40) return 'medium'
  return 'low'
}

/**
 * Risco 0–100 em dez segmentos. Segmentado, e não barra lisa, para a leitura
 * ser "7 de 10" de relance; o número vai ao lado, porque é ele que se cita.
 */
export function RiskMeter({ score, compact = false }: { score: number | null; compact?: boolean }) {
  if (score === null) {
    return <span className="text-micro text-fg-muted">risco —</span>
  }
  const p = severityPresentation(riskSeverity(score))
  const lit = Math.max(1, Math.round(score / 10))
  return (
    <span className="inline-flex items-center gap-1.5" title={`Risco ${score} de 100`}>
      <span aria-hidden className="flex gap-px">
        {Array.from({ length: 10 }, (_, i) => (
          <span
            key={i}
            className={`${compact ? 'h-2 w-[3px]' : 'h-3 w-1'} ${i < lit ? p.fill : 'bg-line'}`}
          />
        ))}
      </span>
      <span className={`tnum font-mono ${compact ? 'text-micro' : 'text-sm'} ${p.text}`}>{score}</span>
    </span>
  )
}

/** Uma técnica/tática MITRE: id monoespaçado, nome ao lado, link para o ATT&CK. */
export function MitreChip({ reference, quiet = false }: { reference: MitreRef; quiet?: boolean }) {
  const url = mitreUrl(reference.id)
  const body = (
    <>
      <span className="font-mono font-medium">{reference.id}</span>
      {reference.name !== null && !quiet && <span className="truncate text-fg-secondary">{reference.name}</span>}
    </>
  )
  const className =
    'inline-flex max-w-full min-w-0 items-center gap-1 border border-line bg-inset px-1.5 py-px text-micro text-fg'
  return url === null ? (
    <span className={className} title={reference.name ?? reference.id}>
      {body}
    </span>
  ) : (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(event) => event.stopPropagation()}
      title={`${reference.id}${reference.name !== null ? ` · ${reference.name}` : ''} — abrir no MITRE ATT&CK`}
      className={`${className} hover:border-accent hover:text-accent`}
    >
      {body}
    </a>
  )
}
